import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";
import { getProfile } from "../createplan/ProfileSlice";

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchFlows = createAsyncThunk("flowBuilder/fetchFlows", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/flows");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const fetchFlow = createAsyncThunk("flowBuilder/fetchFlow", async (id, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/flows/${id}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const createFlow = createAsyncThunk("flowBuilder/createFlow", async ({ flowJson, phoneNumber }, thunkAPI) => {
  try {
    const res = await axiosInstance.post("/flows", { flowJson, phoneNumber: phoneNumber || null });
    return { id: res.data.id, flowJson, phoneNumber: phoneNumber || null, status: "draft", version: 1, validation: res.data.validation };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const updateFlow = createAsyncThunk("flowBuilder/updateFlow", async ({ id, flowJson }, thunkAPI) => {
  try {
    const res = await axiosInstance.put(`/flows/${id}`, { flowJson });
    return { id, flowJson, validation: res.data.validation };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const activateFlow = createAsyncThunk("flowBuilder/activateFlow", async (id, thunkAPI) => {
  try {
    const res = await axiosInstance.post(`/flows/${id}/activate`);
    return { id, warnings: res.data.warnings };
  } catch (err) {
    // 422 = validation errors from server — pass through so UI can display them
    if (err.response?.status === 422) {
      return thunkAPI.rejectWithValue({ _422: true, errors: err.response.data.errors, warnings: err.response.data.warnings });
    }
    // 402 = deployment quota exhausted
    if (err.response?.status === 402) {
      return thunkAPI.rejectWithValue({ _402: true, message: err.response.data.message });
    }
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const deactivateFlow = createAsyncThunk("flowBuilder/deactivateFlow", async (id, thunkAPI) => {
  try {
    await axiosInstance.post(`/flows/${id}/deactivate`);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const deleteFlow = createAsyncThunk("flowBuilder/deleteFlow", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete(`/flows/${id}`);
    return id;
  } catch (err) {
    if (err.response?.status === 409) {
      return thunkAPI.rejectWithValue({ _409: true, message: err.response.data.message });
    }
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const setFlowMode = createAsyncThunk("flowBuilder/setFlowMode", async (mode, thunkAPI) => {
  try {
    // Both card view and visual builder are JSON-driven — always persist "custom" to DB.
    // The local Redux value (mode) still controls which UI component is rendered.
    await axiosInstance.put("/Profile/flow-mode", { mode: "custom" });
    return mode;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const flowBuilderSlice = createSlice({
  name: "flowBuilder",
  initialState: {
    flows:       [],
    currentFlow: null,   // { Id, FlowJson, Status, Version, PhoneNumber, ... }
    flowMode:    "custom",
    status:      "idle",   // for list fetch: idle | loading | success | error
    saving:      false,    // true while createFlow/updateFlow in-flight
    activating:  false,    // true while activateFlow in-flight
    error:       null,
  },
  reducers: {
    clearCurrentFlow(state) {
      state.currentFlow = null;
    },
    setCurrentFlowLocal(state, action) {
      // Update currentFlow.FlowJson locally without a round-trip (for canvas changes)
      if (state.currentFlow) {
        state.currentFlow.FlowJson = action.payload;
      }
    },
    setFlowModeLocal(state, action) {
      state.flowMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ── fetchFlows ──────────────────────────────────────────
    builder
      .addCase(fetchFlows.pending,   (s) => { s.status = "loading"; s.error = null; })
      .addCase(fetchFlows.fulfilled, (s, a) => { s.status = "success"; s.flows = a.payload; })
      .addCase(fetchFlows.rejected,  (s, a) => { s.status = "error"; s.error = a.payload; })

    // ── fetchFlow ───────────────────────────────────────────
      .addCase(fetchFlow.pending,   (s) => { s.status = "loading"; })
      .addCase(fetchFlow.fulfilled, (s, a) => { s.status = "success"; s.currentFlow = a.payload; })
      .addCase(fetchFlow.rejected,  (s, a) => { s.status = "error"; s.error = a.payload; })

    // ── createFlow ──────────────────────────────────────────
      .addCase(createFlow.pending,   (s) => { s.saving = true; })
      .addCase(createFlow.fulfilled, (s, a) => {
        s.saving = false;
        s.flows.unshift({ Id: a.payload.id, name: a.payload.flowJson?.meta?.name || "Untitled", Status: "draft", Version: 1, PhoneNumber: a.payload.phoneNumber || null });
        s.currentFlow = { Id: a.payload.id, FlowJson: a.payload.flowJson, Status: "draft", Version: 1, PhoneNumber: a.payload.phoneNumber || null };
      })
      .addCase(createFlow.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

    // ── updateFlow ──────────────────────────────────────────
      .addCase(updateFlow.pending,   (s) => { s.saving = true; })
      .addCase(updateFlow.fulfilled, (s, a) => {
        s.saving = false;
        if (s.currentFlow?.Id === a.payload.id) s.currentFlow.FlowJson = a.payload.flowJson;
        const idx = s.flows.findIndex(f => f.Id === a.payload.id);
        if (idx !== -1) s.flows[idx] = { ...s.flows[idx], name: a.payload.flowJson?.meta?.name || s.flows[idx].name };
      })
      .addCase(updateFlow.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

    // ── activateFlow ────────────────────────────────────────
      .addCase(activateFlow.pending,   (s) => { s.activating = true; })
      .addCase(activateFlow.fulfilled, (s, a) => {
        s.activating = false;
        // Demote all other flows to draft (mirror server-side transaction)
        s.flows = s.flows.map(f =>
          f.Id === a.payload.id ? { ...f, Status: "active" } : { ...f, Status: "draft" }
        );
        if (s.currentFlow?.Id === a.payload.id) s.currentFlow.Status = "active";
      })
      .addCase(activateFlow.rejected,  (s, a) => { s.activating = false; s.error = a.payload; })

    // ── deactivateFlow ──────────────────────────────────────
      .addCase(deactivateFlow.fulfilled, (s, a) => {
        const idx = s.flows.findIndex(f => f.Id === a.payload);
        if (idx !== -1) s.flows[idx].Status = "draft";
        if (s.currentFlow?.Id === a.payload) s.currentFlow.Status = "draft";
      })

    // ── deleteFlow ──────────────────────────────────────────
      .addCase(deleteFlow.fulfilled, (s, a) => {
        s.flows = s.flows.filter(f => f.Id !== a.payload);
        if (s.currentFlow?.Id === a.payload) s.currentFlow = null;
      })

    // ── setFlowMode ─────────────────────────────────────────
      .addCase(setFlowMode.fulfilled, (s, a) => { s.flowMode = a.payload; })

    // ── Hydrate flowMode from profile response ───────────────
    // getProfile already fetches flowMode from /api/Profile — pick it up here
    // so the mode toggle reflects the server state on every profile load.
      .addCase(getProfile.fulfilled, (s, a) => {
        if (a.payload?.flowMode) s.flowMode = a.payload.flowMode;
      });
  },
});

export const { clearCurrentFlow, setCurrentFlowLocal, setFlowModeLocal } = flowBuilderSlice.actions;
export default flowBuilderSlice.reducer;
